import { z } from "zod";
import type { CriarProdutoRequest } from "../types";

export const produtoSchema = z.object({
  // ── Geral ──────────────────────────────────────────────────────────────────
  descricao_completa: z.string().min(1, "Descrição é obrigatória").max(500),
  descricao_resumida: z.string().max(100).optional().nullable(),
  codigo_barras:      z.string().max(50).optional().nullable(),
  gtin:               z.string().max(20).optional().nullable(),
  tipo_produto:       z.coerce.number().default(1),
  tipo_material_id:   z.coerce.number().default(0),
  classificacao:      z.string().max(50).optional().nullable(),
  inativo:            z.boolean().default(false),

  // ── Classificação ──────────────────────────────────────────────────────────
  grupo_id:           z.coerce.number().default(0),
  sub_grupo_id:       z.coerce.number().default(0),
  secao_id:           z.coerce.number().default(0),
  sub_secao_id:       z.coerce.number().default(0),
  marca_id:           z.coerce.number().default(0),
  modelo_id:          z.coerce.number().default(0),
  cor:                z.string().max(50).optional().nullable(),
  tamanho:            z.string().max(50).optional().nullable(),
  sabor:              z.string().max(50).optional().nullable(),

  // ── Fiscal ─────────────────────────────────────────────────────────────────
  ncm_id:                     z.coerce.number().default(0),
  cest:                       z.string().max(20).optional().nullable(),
  aliquota_icms:              z.coerce.number().min(0).max(100).default(0),
  aliquota_icms_st:           z.coerce.number().min(0).max(100).default(0),
  aliquota_ipi:               z.coerce.number().min(0).max(100).default(0),
  percentual_tributos:        z.coerce.number().min(0).max(100).default(0),
  csosn_id:                   z.coerce.number().default(0),
  origem_id:                  z.coerce.number().default(0),
  cst_id:                     z.coerce.number().default(0),
  cclass_trib:                z.string().max(7).optional().nullable(),
  id_pos:                     z.string().max(60).optional().nullable(),
  anp_id:                     z.coerce.number().default(0),
  natureza_receita_id:        z.coerce.number().default(0),
  cfop_saida_exterior:        z.coerce.number().default(0),
  cfop_entrada_exterior:      z.coerce.number().default(0),
  perc_pis_dif:               z.coerce.number().min(0).max(100).default(0),
  perc_cofins_dif:            z.coerce.number().min(0).max(100).default(0),
  exportavel:                 z.boolean().default(false),
  veiculo_usado:              z.boolean().default(false),
  aliq_cbs:                   z.coerce.number().min(0).max(100).default(0),
  aliq_ibs_uf:                z.coerce.number().min(0).max(100).default(0),
  aliq_ibs_municipio:         z.coerce.number().min(0).max(100).default(0),
  valor_lucro_zero:           z.coerce.number().min(0).default(0),
  codigo_ml:                  z.string().max(40).optional().nullable(),
  garantia:                   z.string().max(60).optional().nullable(),
  garantia_id:                z.coerce.number().int().default(0),
  // Produto Específico
  tipo_especifico:            z.coerce.number().int().min(0).max(5).default(0),
  tipo_arma:                  z.string().max(60).optional().nullable(),
  serie_arma:                 z.string().max(40).optional().nullable(),
  serie_cano:                 z.string().max(40).optional().nullable(),
  descricao_anp:              z.string().max(120).optional().nullable(),
  perc_gas_natural:           z.coerce.number().min(0).max(100).default(0),
  perc_glp:                   z.coerce.number().min(0).max(100).default(0),
  perc_glgn_nacional:         z.coerce.number().min(0).max(100).default(0),
  perc_glgn_importado:        z.coerce.number().min(0).max(100).default(0),
  codif:                      z.string().max(30).optional().nullable(),
  valor_partida:              z.coerce.number().min(0).default(0),
  numero_lote:                z.string().max(40).optional().nullable(),
  quantidade_lote:            z.coerce.number().min(0).default(0),
  data_fabricacao:            z.string().optional().nullable(),
  data_validade:              z.string().optional().nullable(),
  preco_max_consumidor:       z.coerce.number().min(0).default(0),
  numero_recopi:              z.string().max(30).optional().nullable(),
  mve:                        z.coerce.number().min(0).default(0),
  // Precificação
  custo_medio:                z.coerce.number().min(0).default(0),
  custo_gerencial:            z.coerce.number().min(0).default(0),
  custo_reposicao:            z.coerce.number().min(0).default(0),
  custos_extras:              z.coerce.number().min(0).default(0),
  custo_frete:                z.coerce.number().min(0).default(0),
  custo_seguro:               z.coerce.number().min(0).default(0),
  custo_outras_despesas:      z.coerce.number().min(0).default(0),
  custo_embalagem:            z.coerce.number().min(0).default(0),
  perc_custo_operacional:     z.coerce.number().min(0).max(100).default(0),
  lucro_desejado:             z.coerce.number().min(0).default(0),
  perc_lucro_cadastrado:      z.coerce.number().min(0).default(0),
  markup:                     z.coerce.number().min(0).default(0),
  preco_sugestao:             z.coerce.number().min(0).default(0),
  preco_venda1:               z.coerce.number().min(0).default(0),
  preco_venda2:               z.coerce.number().min(0).default(0),
  preco_atacado:              z.coerce.number().min(0).default(0),
  preco_promocao:             z.coerce.number().min(0).default(0),
  desconto_maximo:            z.coerce.number().min(0).max(100).default(0),
  perc_comissao:              z.coerce.number().min(0).max(100).default(0),
  comissao_por:               z.coerce.number().int().min(0).max(1).default(0),
  movimenta_comissao_diferenciada: z.boolean().default(false),
  cst_pis_cofins_entrada_id:  z.coerce.number().default(0),
  cst_pis_cofins_saida_id:    z.coerce.number().default(0),
  cst_ipi_entrada_id:         z.coerce.number().default(0),
  cst_ipi_saida_id:           z.coerce.number().default(0),
  incide_ipi:                 z.boolean().default(true),
  incide_pis_cofins:          z.boolean().default(false),
  monofasico:                 z.boolean().default(false),
  cfop_saida:                 z.coerce.number().default(0),
  cfop_saida_nao_contribuinte: z.coerce.number().default(0),
  cfop_saida_uf:              z.coerce.number().default(0),
  cfop_saida_uf_nao_contribuinte: z.coerce.number().default(0),
  cfop_entrada:               z.coerce.number().default(0),
  cfop_entrada_uf:            z.coerce.number().default(0),

  // ── Preços ─────────────────────────────────────────────────────────────────
  preco_padrao:           z.coerce.number().min(0).default(0),
  tipo_margem_lucro:      z.coerce.number().default(1),
  percentual_margem:      z.coerce.number().min(0).max(100).default(0),
  percentual_desconto:    z.coerce.number().min(0).max(100).default(0),
  valor_margem:           z.coerce.number().min(0).default(0),

  // ── Controle ───────────────────────────────────────────────────────────────
  controla_estoque:       z.boolean().default(true),
  controla_lote:          z.boolean().default(false),
  perecivel:              z.boolean().default(false),
  dias_validade:          z.coerce.number().min(0).default(0),
  conservacao:            z.string().max(500).optional().nullable(),
  rua:                    z.string().max(40).optional().nullable(),
  coluna:                 z.string().max(40).optional().nullable(),
  nivel:                  z.string().max(40).optional().nullable(),
  prateleira:             z.string().max(40).optional().nullable(),
  consulta_peso:          z.boolean().default(false),
  gera_carga_balanca:     z.boolean().default(false),
  mercadoria_producao:    z.boolean().default(false),
  imprimir_data_embalagem: z.boolean().default(false),
  imprimir_observacao:    z.boolean().default(false),
  monta_obs_ficha_tecnica: z.boolean().default(false),

  // ── Exibição ───────────────────────────────────────────────────────────────
  mostrar_celular:     z.boolean().default(true),
  mostrar_catalogo:    z.boolean().default(true),
  mostrar_ecommerce:   z.boolean().default(true),
  gera_comissao:       z.boolean().default(false),
  pontua_fidelidade:   z.boolean().default(false),
  possui_composicao:   z.boolean().default(false),
  possui_adicionais:   z.boolean().default(false),
  usa_grade:           z.boolean().default(false),

  // ── Observações / Imagem ────────────────────────────────────────────────────
  path_imagem: z.string().max(200).optional().nullable(),
  observacao:  z.string().max(1000).optional().nullable(),
});

export type ProdutoFormData = z.infer<typeof produtoSchema>;

// ── Mapper: formulário (snake_case) → request da API (camelCase) ─────────────
// Único caminho válido para enviar o form ao backend (CriarProdutoRequest).
export function mapToApiRequest(data: ProdutoFormData): CriarProdutoRequest {
  const orNull = (v: number | undefined) => (v && v > 0 ? v : undefined);
  return {
    descricao:                    data.descricao_completa,
    descricaoReduzida:            data.descricao_resumida  || undefined,
    codigoBarras:                 data.codigo_barras       || undefined,
    gtin:                         data.gtin                || undefined,
    observacao:                   data.observacao          || undefined,
    tipoProduto:                  data.tipo_produto,
    tipoMaterialId:               data.tipo_material_id,
    classificacao:                data.classificacao       || undefined,
    grupoId:                      orNull(data.grupo_id),
    subgrupoId:                   orNull(data.sub_grupo_id),
    secaoId:                      orNull(data.secao_id),
    subsecaoId:                   orNull(data.sub_secao_id),
    marcaId:                      orNull(data.marca_id),
    modeloId:                     orNull(data.modelo_id),
    cor:                          data.cor                 || undefined,
    tamanho:                      data.tamanho             || undefined,
    sabor:                        data.sabor               || undefined,
    ncmId:                        orNull(data.ncm_id),
    cest:                         data.cest                || undefined,
    csosnId:                      orNull(data.csosn_id),
    origemId:                     orNull(data.origem_id),
    cstId:                        orNull(data.cst_id),
    cstPisCofinsEntradaId:        orNull(data.cst_pis_cofins_entrada_id),
    cstPisCofinsSaidaId:          orNull(data.cst_pis_cofins_saida_id),
    cstIpiEntradaId:              orNull(data.cst_ipi_entrada_id),
    cstIpiSaidaId:                orNull(data.cst_ipi_saida_id),
    aliquotaIcms:                 data.aliquota_icms,
    aliquotaIcmsSt:               data.aliquota_icms_st,
    aliquotaIpi:                  data.aliquota_ipi,
    percentualTributosInformado:  data.percentual_tributos,
    incideIpi:                    data.incide_ipi,
    incidePisCofins:              data.incide_pis_cofins,
    monofasico:                   data.monofasico,
    cfopSaida:                    orNull(data.cfop_saida),
    cfopSaidaNaoContribuinte:     orNull(data.cfop_saida_nao_contribuinte),
    cfopSaidaUf:                  orNull(data.cfop_saida_uf),
    cfopSaidaUfNaoContribuinte:   orNull(data.cfop_saida_uf_nao_contribuinte),
    cfopEntrada:                  orNull(data.cfop_entrada),
    cfopEntradaUf:                orNull(data.cfop_entrada_uf),
    precoPadrao:                  data.preco_padrao,
    tipoMargemLucro:              data.tipo_margem_lucro,
    percentualMargem:             data.percentual_margem,
    percentualDesconto:           data.percentual_desconto,
    controlaEstoque:              data.controla_estoque,
    controlaLote:                 data.controla_lote,
    possuiComposicao:             data.possui_composicao,
    possuiAdicionais:             data.possui_adicionais,
    usaGrade:                     data.usa_grade,
    geraComissao:                 data.gera_comissao,
    pontuaFidelidade:             data.pontua_fidelidade,
    perecivel:                    data.perecivel,
    mercadoriaProducao:           data.mercadoria_producao,
    imprimeDataEmbalagem:         data.imprimir_data_embalagem,
    imprimeObservacao:            data.imprimir_observacao,
    montaObsFichaTecnica:         data.monta_obs_ficha_tecnica,
    mostrarCelular:               data.mostrar_celular,
    mostrarCatalogo:              data.mostrar_catalogo,
    mostrarEcommerce:             data.mostrar_ecommerce,
    diasValidade:                 data.dias_validade,
    conservacao:                  data.conservacao         || undefined,
    rua:                          data.rua                 || undefined,
    coluna:                       data.coluna              || undefined,
    nivel:                        data.nivel               || undefined,
    prateleira:                   data.prateleira          || undefined,
    pathImagem:                   data.path_imagem         || undefined,
    consultaPeso:                 data.consulta_peso,
    geraCargaBalanca:             data.gera_carga_balanca,
    cclassTrib:                   data.cclass_trib         || undefined,
    idPos:                        data.id_pos             || undefined,
    anpId:                        data.anp_id,
    naturezaReceitaId:            data.natureza_receita_id,
    cfopSaidaExterior:            data.cfop_saida_exterior,
    cfopEntradaExterior:          data.cfop_entrada_exterior,
    percentualPisDif:             data.perc_pis_dif,
    percentualCofinsDif:          data.perc_cofins_dif,
    exportavel:                   data.exportavel,
    veiculoUsado:                 data.veiculo_usado,
    aliqCbs:                      data.aliq_cbs,
    aliqIbsUf:                    data.aliq_ibs_uf,
    aliqIbsMunicipio:             data.aliq_ibs_municipio,
    valorLucroZero:               data.valor_lucro_zero,
    codigoMl:                     data.codigo_ml           || undefined,
    garantia:                     data.garantia            || undefined,
    garantiaId:                   data.garantia_id,
    tipoEspecifico:               data.tipo_especifico,
    tipoArma:                     data.tipo_arma           || undefined,
    serieArma:                    data.serie_arma          || undefined,
    serieCano:                    data.serie_cano          || undefined,
    descricaoAnp:                 data.descricao_anp       || undefined,
    percGasNatural:               data.perc_gas_natural,
    percGlp:                      data.perc_glp,
    percGlgnNacional:             data.perc_glgn_nacional,
    percGlgnImportado:            data.perc_glgn_importado,
    codif:                        data.codif               || undefined,
    valorPartida:                 data.valor_partida,
    numeroLote:                   data.numero_lote         || undefined,
    quantidadeLote:               data.quantidade_lote,
    dataFabricacao:               data.data_fabricacao     || undefined,
    dataValidade:                 data.data_validade       || undefined,
    precoMaximoConsumidor:        data.preco_max_consumidor,
    numeroRecopi:                 data.numero_recopi       || undefined,
    mve:                          data.mve,
    custoMedio:                   data.custo_medio,
    custoGerencial:               data.custo_gerencial,
    custoReposicao:               data.custo_reposicao,
    custosExtras:                 data.custos_extras,
    custoFrete:                   data.custo_frete,
    custoSeguro:                  data.custo_seguro,
    custoOutrasDespesas:          data.custo_outras_despesas,
    custoEmbalagem:               data.custo_embalagem,
    percCustoOperacional:         data.perc_custo_operacional,
    lucroDesejado:                data.lucro_desejado,
    percLucroCadastrado:          data.perc_lucro_cadastrado,
    markup:                       data.markup,
    precoSugestao:                data.preco_sugestao,
    precoVenda1:                  data.preco_venda1,
    precoVenda2:                  data.preco_venda2,
    precoAtacado:                 data.preco_atacado,
    precoPromocao:                data.preco_promocao,
    descontoMaximo:               data.desconto_maximo,
    percComissao:                 data.perc_comissao,
    comissaoPor:                  data.comissao_por,
    movimentaComissaoDiferenciada: data.movimenta_comissao_diferenciada,
    inativo:                      data.inativo,
  };
}
